import React, { useRef, useEffect, useCallback, useState } from 'react';
import Accordion, { HeaderPrimaryItem } from '../../shared/Accordion';
import { ResourceStrings } from '../../../ResourceStrings';
import styled from 'styled-components/macro';
import Button from '../../shared/Button';
import { ChoiceGroup, IChoiceGroupOption, IChoiceGroupOptionProps } from '@fluentui/react/lib/ChoiceGroup';
import { platformActions } from '../../../communication/Communication';
import { RootState } from '../../../store/rootState';
import { VSInstanceInfo, ActionRecorderState, ReportType, AppState } from '../../../store/appState';
import * as Actions from '../../../store/actions';
import { useSelector } from 'react-redux';
import { App } from '../../../app';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import { SettingsUpdate } from '../../../SettingsUpdate';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { NativeActions } from '../../../communication/PlatformActions';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { stringFromErrorOrAny } from '../../../utils/tools';
import { Icon } from '@fluentui/react/lib/Icon';
import { forMinWidth } from '../../../theme/breakpoints';

export default function ActionRecorder({ reportType, disabled, forNarrowContainer }: { reportType: ReportType; disabled?: boolean, forNarrowContainer?: boolean }): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);
    const refState = useRef(state);
    const [showNotification, setShowNotification] = useState(false);

    refState.current = state;

    const vsInstanceClick = (processId: number): void => {
        Actions.rapSetRecordPid(processId);
    };

    const newVsInstanceClick = (): void => {
        const native = platformActions.getNativeActions();
        if (native == null) {
            console.log("Native process required");
            return;
        }

        App.telemetry.postBusinessEvent(App.getEventNameForReportType("createnewnewinstance-clicked", reportType));

        native.newVsInstance()
            .catch((error) => {
                App.showUnexpectedError(ResourceStrings.newVSInstanceError(stringFromErrorOrAny(error)));
            });
    };

    const collect = (recordPid: number, diagSessionId: number, settingsUpdate: SettingsUpdate | null): void => {
        const native: NativeActions = platformActions.getNativeActions();
        if (native == null) {
            // TODO: display or log error?
            return;
        }

        let additionalProcessIds: number[] = [];
        if (App.settings.currentProcessId === recordPid) { // if recoding for the same VS that started ReportProblem
            // Add additional PIDs that we want to collect for (right now these are xdesproc)
            additionalProcessIds = App.settings.additionalProcessIds;
        } else { // ... recording for different VS instance than the one that started ReportProblem
            if (settingsUpdate != null) {
                additionalProcessIds = settingsUpdate.additionalProcessIds;
            }
        }


        if (App.settings.useWebRecording) {
            navigator.mediaDevices.getDisplayMedia({
                "video": {
                    "displaySurface": "monitor"
                },
                "audio": false,
                "preferCurrentTab": false,
                "selfBrowserSurface": "include",
            } as any).then((stream: MediaStream) => {
                // recording started

                // find supported codec
                const testCodecs = ["av1", "vp9", "vp8"];
                const supportedCodec = testCodecs.find(codec => MediaRecorder.isTypeSupported(`video/webm; codecs=${codec}`));
                if (!supportedCodec) {
                    console.error("No supported codec found for MediaRecorder.");
                    App.showUnexpectedError("No supported codec found for MediaRecorder. Please update your browser to the latest version.");
                    return;
                }

                console.log("Using supported codec: ", supportedCodec);
                const recorder = new MediaRecorder(stream, {
                    mimeType: `video/webm; codecs=${supportedCodec}`,
                    videoBitsPerSecond: 800000, // bitrate produces crisp 4K video, ~250 MB/hour with av1 codec
                });

                recorder.start();
                recorder.ondataavailable = (event) => {
                    console.log("✅Screen recording finished. Data: ", event.data); // event.data is a Blob
                    native.stopCollectDiagnostics();

                    // show recording item in UI
                    Actions.rapIncludeScreenRecording(true, event.data);
                };

                recorder.onerror = (error) => {
                    console.error("MediaRecorder error: ", error);
                    native.stopCollectDiagnostics();
                    App.showUnexpectedError("MediaRecorder error: " + stringFromErrorOrAny(error));
                };

                recorder.onstop = (ev) => {
                    console.log("MediaRecorder stopped.", ev);
                };

                // start diagnostics collection
                native.startCollectDiagnostics(recordPid, additionalProcessIds, diagSessionId)
                    .catch(error => {
                        recorder.stop();
                        const errorMessage = stringFromErrorOrAny(error);
                        App.showUnexpectedError(ResourceStrings.startRecordingError(errorMessage));
                        Actions.setActionRecorderState(ActionRecorderState.failed, { diagSessionId, errorMessage });
                    });
            }).catch((error: any) => {
                // recording likely cancelled by the user
                native.stopCollectDiagnostics();
                App.showUnexpectedError("Recording cancelled: " + stringFromErrorOrAny(error), true, false, "Recording Cancelled");
                Actions.setActionRecorderState(ActionRecorderState.cancelled, { diagSessionId });
            });
        }
        else {
            native.startCollectDiagnostics(recordPid, additionalProcessIds, diagSessionId)
                .catch(error => {
                    const errorMessage = stringFromErrorOrAny(error);
                    App.showUnexpectedError(ResourceStrings.startRecordingError(errorMessage));
                    Actions.setActionRecorderState(ActionRecorderState.failed, { diagSessionId, errorMessage });
                });
        }


        // TODO: for event handling see PlatformEvent.collectProcessing, PlatformEvent.collectDone, PlatformEvent.collectError in Communication.ts
        // remove the below comments once events above are handled in Communication.ts

        ////////!!! Need to do the switches before spawing the process, so we know for sure we are listening for those events
        ////////If we can't start the collector
        //////ipcRenderer.once('start-collectdiagnostics-error', (event, error: string) => {
        //////    Actions.setRapView(RapView.recfailed);
        //////    Actions.rapSetRecordingInProgress(false);
        //////    App.telemetry.postFault("StartCollectorFailure", error);
        //////});

        //////// Once the Collector is done collecting steps and it's wrapping up collection (getting dumps, ETL, zipping, etc.)
        //////// the main process will let the renderer know, so it can display a new view to the user
        //////// letting them know they need to wait a couple of minutes
        //////ipcRenderer.once('switch-to-processing', () => {
        //////    Actions.setRapView(RapView.processing);
        //////    Actions.setCloseable(false); // The user shouldn't be able to close the dialog
        //////});

        //////// Once everything is done, the main process lets the renderer know to switch back to ReportProblem view
        //////ipcRenderer.once('switch-to-report-problem', (event) => {
        //////    let recordFilesExist: boolean = false;
        //////    try {
        //////        // get all file names in the final folder and mark them for upload
        //////        const files: string[] = fs.readdirSync(collectionFullPath);
        //////        if (files.length !== 0) {
        //////            recordFilesExist = true;
        //////            const fileInfos: FileInfo[] = files.map((file: string) => {
        //////                return { path: collectionFullPath + "\\" + file, name: file };
        //////            });
        //////            Actions.rapAddFilesToUpload(fileInfos);
        //////            App.telemetry.postUserTask("RecordEnd", TelemetryResult.Success, `Collector done: ${files.join()}`, "", {
        //////                "FileCount": files.length.toString(),
        //////            });
        //////        } else {
        //////            App.telemetry.postUserTask("RecordEnd", TelemetryResult.Failure, "The collector didn't record anything!");
        //////        }
        //////    } catch (err) {
        //////        console.log(`Error getting files to upload ${err.stack}`);
        //////        App.telemetry.postUserTask("RecordEnd", TelemetryResult.Failure, `Error getting files to upload: ${err.stack}`);
        //////    }
        //////    if (recordFilesExist) {
        //////        // Return to the Record view to show the files
        //////        Actions.setRapView(RapView.record);
        //////        // The Close button can now be visible again
        //////        Actions.setCloseable(true);
        //////    } else {
        //////        Actions.setRapView(RapView.recfailed);
        //////    }
        //////    Actions.rapSetRecordingInProgress(false);
        //////});

        //////// The Collector needs to be started in the main process so it can communicate with this app (ReportProblem)
        //////const spawnArgs: SpawnArgs = new SpawnArgs(configFilePath);
        //////ipcRenderer.send('start-collectdiagnostics', spawnArgs);
    }

    // Controls what happens when the Record button is pushed
    const startRecordingClick = (): void => {
        App.telemetry.postBusinessEvent(App.getEventNameForReportType("startrecording-clicked", reportType), {
            instance: (App.settings.currentProcessId === refState.current.reportRecordPid) ? "original" : "new",
        });

        const diagSessionId = refState.current.diagSessionId + 1;
        Actions.setdiagSessionId(diagSessionId);

        // @TODO: Remove rapSetRecordingInProgress in favor of setActionRecorderState to get more detailed state
        Actions.rapSetRecordingInProgress(true);
        Actions.setActionRecorderState(ActionRecorderState.started, { diagSessionId });
        // Change to a view letting the user know that they should be recording steps
        ////Actions.setRapView(RapView.recprogress);
        // The user shouldn't be able to close the dialog
        Actions.setCloseable(false);

        setShowNotification(true);

        const native: NativeActions = platformActions.getNativeActions();
        if (native == null) {
            // TODO: display or log error?
            return;
        }
        // If we are recording for a different VS instance than the one that started ReportProblem
        // we need to get a list of updated settings that are specific to that instance
        if (!(App.settings.currentProcessId === refState.current.reportRecordPid)) {
            let settingsUpdate: SettingsUpdate | null = null;
            native.updateSettings(refState.current.reportRecordPid).then((data: SettingsUpdate) => {
                settingsUpdate = Object.assign(new SettingsUpdate(), data);
            }).catch((reason: any) => {
                App.telemetry.postFault("RecordFailure", `Error getting updated settings file: ${reason?.stack}`, reason);
                settingsUpdate = null; //make sure we aren't sending old data from previous recordings
                console.error("updateSettings ERROR:");
                console.error(reason);
                // TODO: uncomment once https://dev.azure.com/devdiv/DevDiv/_workitems/edit/1172945 is fixed
                // App.showUnexpectedError(ResourceStrings.startRecordingError(stringFromErrorOrAny(reason)));
            }).finally(() => {
                Actions.setSettingsUpdate(settingsUpdate);
                collect(refState.current.reportRecordPid, diagSessionId, settingsUpdate);
            });
        } else {
            Actions.setSettingsUpdate(null); //make sure we aren't sending old data from previous recordings
            collect(refState.current.reportRecordPid, diagSessionId, null);
        }
    };

    const getProcesses = useCallback((): IChoiceGroupOption[] => {
        const optionRootClass = mergeStyles({ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', height: '100%' });

        return refState.current.reportRunningVsInstances
            .map((instance, i) => ({
                key: instance.pid.toString(),
                value: instance.pid,
                text: instance.title,
                ariaLabel: `${instance.title} ${ResourceStrings.pidHeader()} ${instance.pid.toString()}`,
                // eslint-disable-next-line react/display-name
                onRenderField: (props: IChoiceGroupOption & IChoiceGroupOptionProps, render) => {
                    return (<>
                        <span className={`${optionRootClass} process-row for-small-screen`}>
                            {render!({ ...props, id: `${props.id}-small-screen`, labelId: `${props.labelId}-small-screen` })}
                            <span className="process-record-id-container">
                                <span className='process-record-id-label'>{ResourceStrings.pidHeader()}:&nbsp;</span>
                                <span className='process-record-id'>{props.value}</span>
                            </span>
                        </span>
                        <span className={`${optionRootClass} process-row for-large-screen`}>
                            {render!(props)}
                            <span className="process-record-id-container">
                                <span className='process-record-id-label'>{ResourceStrings.pidHeader()}:&nbsp;</span>
                                <span className='process-record-id'>{props.value}</span>
                            </span>
                        </span>
                    </>);
                }
            } as IChoiceGroupOption))
    }, [state.reportRunningVsInstances]);

    const isRecording = state.actionRecorderState === ActionRecorderState.started
        || state.actionRecorderState === ActionRecorderState.processing;
    const safeToCloseVS = state.actionRecorderSafeToCloseVS;

    return (
        <Accordion
            titleIcon="Video"
            title={<HeaderPrimaryItem>{ResourceStrings.recordYourActions()}</HeaderPrimaryItem>}>
            <div>
                {state.actionRecorderState === ActionRecorderState.failed && showNotification && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        styles={{ root: { margin: "auto 2rem", width: "auto" } }}
                        isMultiline={true}
                        onDismiss={() => setShowNotification(false)}
                        dismissButtonAriaLabel={ResourceStrings.close()}
                    >
                        {ResourceStrings.errorInfo(state.actionRecorderErrorMessage)}
                    </MessageBar>
                )}

                <Content className={forNarrowContainer ? "narrow-container" : "wide-container"}>
                    {!isRecording && <Processes
                        processes={getProcesses()}
                        selectedProcessId={state.reportRecordPid}
                        disabled={disabled}
                        onProcessSelected={(selectedProcessId: number) => vsInstanceClick(selectedProcessId)}
                        onNewProcessClicked={newVsInstanceClick}
                        onStartRecordingClicked={startRecordingClick}
                    />}

                    {isRecording && <div role="status">
                        <Progress>
                            {state.actionRecorderState === ActionRecorderState.started && <div>{ResourceStrings.recording()}</div>}
                            {state.actionRecorderState === ActionRecorderState.processing && (
                                <>
                                    {!safeToCloseVS && <div><strong>{ResourceStrings.pleaseWait()}</strong></div>}
                                    <ProgressIndicator
                                        label={safeToCloseVS ? ResourceStrings.processingButCanSubmit() : ResourceStrings.processing()}
                                        barHeight={4}
                                        styles={{
                                            itemName: {
                                                whiteSpace: "normal",
                                                paddingBottom: "1rem",
                                                fontSize: "var(--font-size-default)"
                                            }
                                        }}
                                    />
                                </>
                            )}
                        </Progress>
                    </div>}
                </Content>
            </div>
        </Accordion >
    );
}

interface ProcessesProps {
    processes: IChoiceGroupOption[];
    selectedProcessId: number;
    disabled?: boolean;
    onProcessSelected: (selectedProcessId: number) => void;
    onNewProcessClicked: () => void;
    onStartRecordingClicked: () => void;
}

const Processes = (props: ProcessesProps): JSX.Element => {
    const state = useSelector((state: RootState) => state.appState);
    const refState = useRef(state);
    const processesTimer = useRef<NodeJS.Timeout>(null);
    const [instancesFetchError, setInstancesFetchError] = useState<string>(null);

    refState.current = state;

    useEffect(() => {
        reloadRunningVsInstances();

        processesTimer.current = setInterval(() => {
            reloadRunningVsInstances();
        }, 2 * 1000);

        return () => {
            if (processesTimer.current != null) {
                clearInterval(processesTimer.current);
            }
        }
    }, []);

    const reloadRunningVsInstances = (): void => {
        const native = platformActions.getNativeActions();
        if (native == null) {
            return;
        }

        native.getRunningVsInstances()
            .then((runningVSInstances: VSInstanceInfo[]) => {
                // check if running VS instances have changed
                if (refState.current.reportRunningVsInstances.length === runningVSInstances.length) {
                    let areSameVSInstances: boolean = true;
                    for (let i = 0; i < runningVSInstances.length; i++) {
                        // eslint-disable-next-line security/detect-object-injection
                        const info: VSInstanceInfo = refState.current.reportRunningVsInstances[i];
                        // eslint-disable-next-line security/detect-object-injection
                        if (info.pid !== runningVSInstances[i].pid) {
                            areSameVSInstances = false;
                            break;
                        }
                    }

                    if (areSameVSInstances) {
                        return;
                    }
                }

                // Differentiate the original VS instance (the parent that started FeedbackConsole)
                let selectedVSInstanceIsRunning: boolean = false;
                let isParentVSInstanceRunning: boolean = false;
                for (let i = 0; i < runningVSInstances.length; i++) {
                    // eslint-disable-next-line security/detect-object-injection
                    if (runningVSInstances[i].pid === App.settings.currentProcessId) {
                        // eslint-disable-next-line security/detect-object-injection
                        runningVSInstances[i].title = ResourceStrings.currentVSInstance();
                        isParentVSInstanceRunning = true;
                        if (i > 0) {
                            const temp: VSInstanceInfo = runningVSInstances[0];
                            // eslint-disable-next-line security/detect-object-injection
                            runningVSInstances[0] = runningVSInstances[i];
                            // eslint-disable-next-line security/detect-object-injection
                            runningVSInstances[i] = temp;
                        }
                    }
                    // eslint-disable-next-line security/detect-object-injection
                    if (runningVSInstances[i].pid === refState.current.reportRecordPid) {
                        selectedVSInstanceIsRunning = true;
                    }
                }
                Actions.setParentVSInstanceRunningState(isParentVSInstanceRunning);

                // If the previously selected VSInstance isn't running anymore, select another
                if ((runningVSInstances.length !== 0) && !selectedVSInstanceIsRunning) {
                    Actions.rapSetRecordPid(runningVSInstances[0].pid);
                }

                Actions.rapAddRunningVSInstances(runningVSInstances);
            })
            .catch(error => {
                Actions.rapAddRunningVSInstances([]);
                setInstancesFetchError(ResourceStrings.vsInstancesFetchError(stringFromErrorOrAny(error)));
            });
    };

    return <>
        {instancesFetchError && (
            <MessageBar
                messageBarType={MessageBarType.error}
                styles={{ root: { margin: "auto 2rem", width: "auto" } }}
                isMultiline={true}
            >
                {instancesFetchError}
            </MessageBar>
        )}

        <ProcessList>
            <ChoiceGroup
                css={"visibility: " + (props.processes.length === 0 ? "hidden" : "visible")}
                options={props.processes}
                selectedKey={props.selectedProcessId?.toString()}
                disabled={props.disabled}
                onChange={(e, option) => props.onProcessSelected(Number.parseInt(option.key))}
            />
            <Button buttonType="link" className="btn-add-instance" disabled={props.disabled} onClick={() => props.onNewProcessClicked()}>
                <Icon iconName="Add" />
                {ResourceStrings.newVSInstance()}
            </Button>
        </ProcessList>
        <Buttons>
            {props.processes && props.processes.length !== 0 && props.selectedProcessId && (
                <Button buttonType="link" onClick={props.onStartRecordingClicked} disabled={props.processes.length === 0 || props.disabled}>{ResourceStrings.startRecord()}</Button>
            )}
        </Buttons>
    </>
}

const Content = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 1.4rem 4.8rem 2.8rem 4.8rem;

    ${forMinWidth.lg`
        &.wide-container {
            flex-direction: row;
        }
    `}

    ${forMinWidth.xl`
        &.narrow-container {
            flex-direction: row;
        }
    `}
`;

const Buttons = styled.div`
    display: flex;
    white-space: nowrap;
    align-items: flex-end;
    flex-wrap: wrap;
    justify-content: flex-end;

    button {
        font-size: var(--font-size-default);
    }
`;

const ProcessList = styled.div`
    .ms-ChoiceField {
        margin-top: 0;
        line-height: 2.8rem;
    }

    .ms-ChoiceField + .ms-ChoiceField {
        border-top: solid 1px var(--color-on-surface-lighter);
        margin-top: .6rem;
        padding-top: .8rem;
    }

    ${forMinWidth.md`
        .ms-ChoiceField + .ms-ChoiceField {
            border-top: none;
            margin-top: 0;
            padding-top: 0;
        }
    `}

    .ms-ChoiceField-field::before {
        top: .5rem;
        height: 1.8rem;
        width: 1.8rem;
    }

    .ms-ChoiceField-field::after {
        top: .9rem;
        left: .4rem;
    }

    .ms-ChoiceFieldLabel {
        padding-left: 3.5rem;
        word-break: break-word;
    }

    .ms-ChoiceField-wrapper {
        width: 100%;
    }

    /* On small screen some portion of the radio button is not clickable without this */
    .ms-ChoiceField-input {
        z-index: 200;
    }

    .for-small-screen {
        &.process-row {
            flex-direction: column;
            line-height: 2rem;

            &> span {
                margin-left: 3.5rem;
                line-height: initial;
            }
        }

        .process-record-id {
            width: 6.5rem;
            display: inline-block;
        }

        .process-record-id-label {
            color: var(--color-on-surface-light);
            height: 100%;
        }
    }

    .for-large-screen {
        .process-record-id {
            width: 6.5rem;
            display: inline-block;
        }

        .process-record-id-label {
            color: var(--color-on-surface-light);

            border-left: solid 1px var(--color-on-surface-light);
            padding-left: 3rem;
            margin-left: 2rem;
            display: inline-block;
            height: 100%;
        }
    }

    .btn-add-instance {
        font-size: 1.4rem;
        font-weight: normal;
        color: var(--color-on-surface);
        margin-top: 1.5rem;

        ${forMinWidth.md`
            margin-top: 0;
        `}

        i {
            font-size: 1.6rem;
            color: var(--color-on-surface-link);
            margin-right: .8rem;
        }
    }

    .process-row.for-large-screen {
        display: none;
    }

    .process-row {
        align-items: stretch;
    }

    .process-record-id-container {
        min-width: 20rem;
    }

    ${forMinWidth.md`
        .process-row.for-small-screen {
            display: none;
        }

        .process-row.for-large-screen {
            display: flex;
        }
    `}
`;

const Progress = styled.div`
    height: 10rem;
`;
